import { faPlus, faSync } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useEffect, useRef, useState } from 'react';

import Button from '@/components/Button';
import Card from '@/components/Card';
import EmptyState from '@/components/EmptyState';
import IconicButton from '@/components/IconicButton';
import LoadingState from '@/components/LoadingState';
import Option from '@/components/Option';
import Page from '@/components/Page';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import TextInput from '@/components/TextInput';
import TextSelect from '@/components/TextSelect';
import { Body, Small } from '@/components/Typography';
import useMediaQuery from '@/hooks/useMediaQuery';
import { jobColors } from '@/styles/colors';

import BadgeFormModal from './BadgeFormModal';
import BadgeRowItem from './BadgeRowItem';
import DeleteBadgeModal from './DeleteBadgeModal';
import {
  Certificate,
  CertificateTypeLabel,
  errorMessageFrom,
  listCertificates,
  Pagination,
} from './api';

const HEADER_FIELDS = ['', 'Name', 'Type', 'In use', 'Status', ''];

const TYPE_FILTER_OPTIONS = [
  { label: 'All types', value: '' },
  { label: 'Badges', value: 'BADGE' },
  { label: 'Certificates', value: 'CERTIFICATE' },
];

const PER_PAGE = 25;

const Badges = () => {
  const phoneOnly = useMediaQuery('(max-width: 559px)');

  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<CertificateTypeLabel | ''>('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [page, setPage] = useState(1);

  const [editing, setEditing] = useState<Certificate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleting, setDeleting] = useState<Certificate | null>(null);

  // Monotonic request id. Responses can land out of order — a slow page-2
  // fetch resolving after a fast page-1 fetch would repaint the stale page —
  // so only the newest request is allowed to write state.
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    try {
      setIsLoading(true);
      setError(null);
      const response = await listCertificates({
        search: search.trim() || undefined,
        certificate_type: typeFilter || undefined,
        include_deleted: includeDeleted || undefined,
        page,
        per_page: PER_PAGE,
      });
      if (seq !== requestSeq.current) return;
      setCertificates(response.data ?? []);
      setPagination(response.pagination ?? null);
    } catch (err: any) {
      if (seq !== requestSeq.current) return;
      setError(errorMessageFrom(err, 'Failed to load badges.'));
    } finally {
      if (seq === requestSeq.current) setIsLoading(false);
    }
  }, [search, typeFilter, includeDeleted, page]);

  // Debounced so typing in the search box does not fire a request per keypress.
  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  // Reset to page 1 as part of the change itself, NOT in an effect. Doing it in
  // an effect meant a filter change while on page 3 first fetched page 3 of the
  // NEW filter (usually empty, so the list flashed "No badges yet") and only
  // then reset and refetched.
  const changeFilter = useCallback((apply: () => void) => {
    apply();
    setPage(1);
  }, []);

  const totalPages = pagination?.total_pages ?? 0;

  return (
    <Page
      headerPadding={!!phoneOnly}
      noPadding={!!phoneOnly}
      size={phoneOnly ? 'full' : 'xl'}
      title="Badges & Certificates"
      primaryAction={{
        a11yLabel: 'Create a new badge',
        label: 'New badge',
        icon: faPlus,
        onAction: () => setIsCreating(true),
      }}
    >
      <Card noRadius={phoneOnly}>
        <Card.Section>
          <Stack justify="apart" align="center">
            <Body weight="semibold">
              Catalog{pagination ? ` (${pagination.total})` : ''}
            </Body>
            <IconicButton
              a11yLabel="Refresh list"
              appearance="plain"
              icon={faSync}
              size="sm"
              onClick={load}
            />
          </Stack>
        </Card.Section>

        <Card.Section>
          <Stack gap={12} align="center" wrap>
            <TextInput
              id="badge-search"
              placeholder="Search by name…"
              value={search}
              width="240px"
              onChange={(event) =>
                changeFilter(() => setSearch(event.target.value))
              }
            />
            <TextSelect
              id="badge-type-filter"
              value={typeFilter}
              onChange={(event) =>
                changeFilter(() =>
                  setTypeFilter(event.target.value as CertificateTypeLabel | ''),
                )
              }
            >
              {TYPE_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextSelect>
            <Option
              checked={includeDeleted}
              id="badge-include-deleted"
              label="Show deleted"
              type="checkbox"
              onChange={() =>
                changeFilter(() => setIncludeDeleted((current) => !current))
              }
            />
          </Stack>
        </Card.Section>

        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <Card.Section>
            <Stack vertical align="center" gap={16}>
              <Body color="danger">{error}</Body>
              <Button a11yLabel="Retry" label="Retry" onClick={load} />
            </Stack>
          </Card.Section>
        ) : certificates.length === 0 ? (
          <EmptyState
            title="No badges yet"
            text="Create a badge to make it selectable as a client or job requirement."
          />
        ) : (
          <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
            <Table>
              {!phoneOnly && (
                <TableHeader
                  fields={HEADER_FIELDS}
                  headerBgColor={jobColors.headerLight}
                />
              )}
              <tbody>
                {certificates.map((certificate) => (
                  <BadgeRowItem
                    key={certificate.id}
                    certificate={certificate}
                    onDelete={setDeleting}
                    onEdit={setEditing}
                  />
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {totalPages > 1 && (
          <Card.Section>
            <Stack justify="end" align="center" gap={12}>
              <Small color="light">
                Page {pagination?.page} of {totalPages}
              </Small>
              <Button
                a11yLabel="Previous page"
                appearance="outline"
                disabled={page <= 1}
                label="Previous"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              />
              <Button
                a11yLabel="Next page"
                appearance="outline"
                disabled={page >= totalPages}
                label="Next"
                onClick={() => setPage((current) => current + 1)}
              />
            </Stack>
          </Card.Section>
        )}
      </Card>

      {(isCreating || editing) && (
        <BadgeFormModal
          certificate={editing}
          onClose={() => {
            setIsCreating(false);
            setEditing(null);
          }}
          onSaved={load}
        />
      )}

      {deleting && (
        <DeleteBadgeModal
          certificate={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={load}
        />
      )}
    </Page>
  );
};

export default Badges;
